import _ from 'underscore';
import { ActionVerbs, ActionStages, splitActionName } from './action-creators';
import { ActionSlices } from '../common/action-utilities';
import { mergeEntities, updateEntity } from '../common/reducer-utilities';
import { getEntityCollectionName } from '../common/pluralize-entity';

const DEFAULT_TOP = 20;
const DEFAULT_SKIP = 0;

export const initialEntityState = {
  lastUpdated: undefined,
  isLoading: false,
  lastParentEntityPath: undefined,
  data: null,
  count: null,
  error: null,

  params: {
    top: DEFAULT_TOP,
    skip: DEFAULT_SKIP,
    select: null,
  },
};

function handleReset(state, params) {
  if (params) {
    const { shouldReset } = params;
    if (shouldReset) {
      return {
        ...state,
        data: _.reject(state.data, e => shouldReset(e)),
      };
    }
  }
  return initialEntityState;
}

function handleRead(state, params, stage) {
  const {
    entityKey,
    actionTime,
    entityId,
    parentEntity,
    ...queryParams
  } = params;

  switch (stage) {
    case ActionStages.start: {
      if (!parentEntity) {
        throw Error(`ParentEntity is null. entityKey: ${entityKey}, entityId: ${entityId}, queryParams: ${JSON.stringify(queryParams)}`);
      }
      // clear state if parentEntity is different than last visited one
      const { path } = parentEntity.navigation;
      let newData = state.data;

      if (state.lastParentEntityPath && state.lastParentEntityPath !== path) {
        newData = null;
      }

      if (!entityId) {
        const {
          skip: paramSkip,
        } = queryParams;

        // if "skip" wasn't provided, we'll increment the skip by the amount of data already read.
        let newSkip = newData !== null ? newData.length : DEFAULT_SKIP;
        if (paramSkip !== undefined) {
          newSkip = paramSkip;
        }

        return {
          ...state,
          error: null,
          isLoading: true,
          lastParentEntityPath: path,
          data: newData,
          params: {
            ...state.params,

            // spread out other query parameters
            ...queryParams,

            // and override calculated ones.
            skip: newSkip,
          },
        };
      }

      return {
        ...state,
        error: null,
        lastParentEntityPath: path,
        data: newData,
        isLoading: true,
      };
    }
    case ActionStages.complete: {
      const { value, count } = params;
      return {
        ...state,
        lastUpdated: actionTime,
        isLoading: false,
        data: mergeEntities(state.data, value, entityKey),
        count,
        error: null,
      };
    }
    case ActionStages.fail: {
      // TODO: when read fails, we need to roll back the query parameters to allow retry.
      const { error } = params;
      return {
        ...state,
        isLoading: false,
        error,
      };
    }
    default:
      return state;
  }
}

function handleUpdate(state, params, stage) {
  const {
    entityKey, // name of the entity key
    actionTime, // timestamp when action happens
  } = params;

  const updateData = (id, diff) => {
    if (state.data) {
      return updateEntity(state.data, id, diff, entityKey);
    }

    return null;
  };

  switch (stage) {
    case ActionStages.start: {
      // update data locally
      const {
        id,
        diff,
      } = params;

      return {
        ...state,
        isLoading: true,
        data: updateData(id, diff),
        error: null,
      };
    }
    case ActionStages.complete:
      return {
        ...state,
        lastUpdated: actionTime,
        isLoading: false,
        error: null,
      };
    case ActionStages.fail: {
      const {
        error,
        id,
        originalDiff,
      } = params;

      return {
        ...state,
        isLoading: false,
        // updateData(id, originalDiff) will only
        // revert value key'e by "originalDiff"
        data: updateData(id, originalDiff),
        error,
      };
    }
    default:
      return state;
  }
}

function handleCreate(state, params, stage) {
  const {
    actionTime,
    entityKey,
    parentEntity,
    // we'll use this as a temporary Id to distinguish if
    // multiple creation were initiated.
    //
    // When START action initiates, we'll fill tempId into a field
    // called "@mca.id", which will be removed from the state after COMPLETE.
    // However if a FAIL action happens, we'll find and remove the
    // precise data element using this field.
    tempId,
  } = params;

  switch (stage) {
    case ActionStages.start: {
      const { data } = params;

      // data should not have an id field
      if (data && data[entityKey]) {
        throw new Error('Id should not exist in new creation');
      }

      const tempData = {
        ...data,
        '@mca.id': tempId,
      };

      // add the new object to the state
      return {
        ...state,
        isLoading: true,
        lastParentEntityPath: parentEntity.navigation.path,
        data: [...state.data || [], tempData],
      };
    }
    case ActionStages.complete: {
      const { response } = params;

      const { value } = response;
      // to make the process simpler, we'll find the element using "@mca.id"
      // and remove it, then replace it with "value" in the same place.
      const newData = _.map(state.data, (element) => {
        if (element['@mca.id'] === tempId) {
          const keyedElement = { ...element };

          delete keyedElement['@mca.id'];
          keyedElement[entityKey] = value;
          return keyedElement;
        }

        return element;
      });

      return {
        ...state,
        lastUpdated: actionTime,
        isLoading: false,
        data: newData,
        error: null,
      };
    }
    case ActionStages.fail: {
      const { error } = params;

      // when FAIL, remove the data element using @mca.id
      const newData = _.filter(state.data, (element) => {
        if (element['@mca.id'] === tempId) {
          return false;
        }

        return true;
      });

      return {
        ...state,
        isLoading: false,
        data: newData,
        error,
      };
    }
    default:
      return state;
  }
}

function handleDestroy(state, params, stage) {
  const { id, entityKey, actionTime } = params;

  switch (stage) {
    case ActionStages.start:
      // remove the entity from state.data
      return {
        ...state,
        isLoading: true,
        data: _.reject(state.data, e => e[entityKey] === id),
      };
    case ActionStages.complete:
      return {
        ...state,
        isLoading: false,
        error: null,
        lastUpdated: actionTime,
      };
    case ActionStages.fail: {
      const { error, originalData } = params;
      return {
        ...state,
        isLoading: false,
        data: originalData,
        error,
      };
    }
    default:
      return state;
  }
}

const handleActions = (verb, state, params, stage) => {
  switch (verb) {
    case ActionVerbs.reset:
      return handleReset(state, params);
    case ActionVerbs.read:
      return handleRead(state, params, stage);
    case ActionVerbs.update:
      return handleUpdate(state, params, stage);
    case ActionVerbs.create:
      return handleCreate(state, params, stage);
    case ActionVerbs.destroy:
      return handleDestroy(state, params, stage);
    default:
      return state;
  }
};

export function mcaReducerCreator(entityName) {
  const entityCollectionName = getEntityCollectionName(entityName);
  return (
    state = {},
    action
  ) => {
    const {
      params = {},
    } = action;

    const {
      sideEffectFn,
      ...otherParams
    } = params;

    const {
      slice,
      label,
      verb,
      stage,
    } = splitActionName(action);

    if (slice !== ActionSlices.mcaEntity) {
      return state || initialEntityState;
    }

    if (label &&
      label !== entityCollectionName &&
      label !== entityName) {
      return state || initialEntityState;
    }

    const entityCollectionState =
      state[entityCollectionName] || initialEntityState;

    const newStates = { ...state };
    newStates[entityCollectionName] =
      handleActions(verb, entityCollectionState, otherParams, stage);

    // perform side effects on entities
    if (sideEffectFn) {
      // we'll iterate thru all keys in the global state (i.e., all entities),
      // and call sideEffectFn on to reduce their data
      const entityCollKeys = Object.keys(newStates);

      for (let i = 0; i < entityCollKeys.length; i += 1) {
        const entityCollKey = entityCollKeys[i];
        const entityCollState = newStates[entityCollKey];

        newStates[entityCollKey] = sideEffectFn(
          entityCollKey,
          stage,
          entityCollState,
          params.actionTime,
          params.response
        );
      }
    }
    return newStates;
  };
}
